"use client";

import Image from "next/image";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { ArrowRight, BadgeCheck, ChevronDown, Loader2, Mail } from "lucide-react";
import { captureCustomer } from "@/lib/actions/captureCustomer";
import { BLANK_IMAGE_SRC, HOMEPAGE_PLAN_PAGE_VERSION, LANDING_STATS, TRIAL_TERMS_SHORT } from "@/lib/landing-constants";
import { calloutDestination } from "@/lib/marketing/community-callout";
import { EMAIL_CAPTURE_SURFACE, EMAIL_CAPTURED, EMAIL_CAPTURED_EVENT, type StateSource } from "@/lib/marketing/email-capture-policy";
import { US_STATES, normalizeUsState, type UsState } from "@/lib/us-states";
import { HOMEPAGE_PROOF_RECORDS } from "@/lib/marketing/homepage-proof-records";
import { DIAMOND_UPGRADE_LABEL, diamondUpgradeHref, isDiamondMember } from "@/lib/marketing/member-plan";
import { NAV_SIGNED_OUT_VARIANT_CLASS } from "@/lib/nav-auth-hint";
import { usePublicAuth } from "@/lib/public-auth";
import { setLauncherHeld } from "@/components/ui/intercom/messenger-visibility";
import { ALL_TESTIMONIALS } from "@/lib/testimonial-data";
import { phCapture, phGetDistinctId, trackMarketingCtaClick } from "@/utils/analytics";
import { getAttributionForEvent } from "@/utils/attribution";
import styles from "./hero-phone.module.css";

const PHONE_QUERY = "(max-width: 767px)";
const ANALYTICS_SURFACE = "home_hero_phone";

export const HEADLINE_MONTH_FLOOR = 5000;
export const HEADLINE_MONTH_PEAK = 10000;
const COUNT_MS = 1000;
const CYCLE_MS = 4500;
const SHRINK_MS = 300;
const GROWTH = 0.08;
const PIKKIT_STORY_IDS = [2, 1, 37];
const MONTH_RECORDS = HOMEPAGE_PROOF_RECORDS.filter((record) => record.metric === "Net profit");
const RECORD_DETAILS: Record<string, { avatar: string; note?: string }> = {
  "matt-november-2025": { avatar: "/images/sportsbooks/id_PlusEV.webp", note: "Upside founder" },
  "tj-nfl-september-2026": { avatar: "/images/sportsbooks/id_thomasjordaniv.webp" },
  "alex-september-2026": { avatar: "/images/sportsbooks/id_Cinematicc.webp" },
};

const TRUST_AVATARS = [
  "/images/sportsbooks/id_bg.webp",
  "/images/sportsbooks/id_EarlyJuice.webp",
  "/images/sportsbooks/id_ericallred4.webp",
];

const STORIES = PIKKIT_STORY_IDS.map((id) => ALL_TESTIMONIALS.find((story) => story.id === id)!);
const OPENING_STORY = {
  name: "Benny Bills",
  handle: "@BennyDollaBillz",
  postedAt: "Jan 2026",
  profit: "$8,000+",
  avatarSrc: "/images/sportsbooks/id_BennyDollaBillz.webp",
  imageSrc: "/images/landing/homepage-proof/benny-one-night-8000-user-reported.webp",
  text: "Tonight was legendary! Cashed out for over $8,000! Huge shoutout to @UpsideTools for the picks!",
};

function hasCookie(name: string) {
  return document.cookie.split(";").some((cookie) => cookie.trim().startsWith(`${name}=`));
}

function setCookie(name: string, days: number) {
  document.cookie = `${name}=1;path=/;max-age=${days * 86400};SameSite=Lax${location.protocol === "https:" ? ";Secure" : ""}`;
}

function PhoneOnlyImage({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <picture className={className}>
      <source media="(min-width: 768px)" srcSet={BLANK_IMAGE_SRC} />
      {children}
    </picture>
  );
}

export function HeroPhoneTrust() {
  return (
    <div className="mb-3 flex items-center gap-2.5 md:hidden">
      <div className="flex -space-x-2">
        {TRUST_AVATARS.map((src) => (
          <PhoneOnlyImage key={src} className="block">
            <Image src={src} alt="" width={28} height={28} loading="eager" className="size-7 rounded-full object-cover ring-2 ring-background" />
          </PhoneOnlyImage>
        ))}
      </div>
      <p className="text-sm text-muted-foreground">Trusted by {LANDING_STATS.ACTIVE_USERS_DISPLAY} bettors</p>
    </div>
  );
}

const dollars = (value: number) => `$${value.toLocaleString("en-US")}`;

function HeadlineAmount() {
  const valueRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const value = valueRef.current;
    if (!value || typeof window.matchMedia !== "function") return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const phone = window.matchMedia(PHONE_QUERY);
    let frame = 0;
    let startedAt = 0;
    let onScreen = true;
    let shown = "";

    const tick = (now: number) => {
      const elapsed = (now - startedAt) % CYCLE_MS;
      const counted = Math.min(elapsed / COUNT_MS, 1);
      const eased = 1 - (1 - counted) ** 3;
      const amount = Math.round((HEADLINE_MONTH_FLOOR + (HEADLINE_MONTH_PEAK - HEADLINE_MONTH_FLOOR) * eased) / 50) * 50;
      const text = counted < 1 ? dollars(amount) : `${dollars(HEADLINE_MONTH_PEAK)}+`;
      if (text !== shown) { value.textContent = text; shown = text; }
      const growing = Math.min(elapsed / (CYCLE_MS - SHRINK_MS), 1);
      const shrinking = Math.max((elapsed - (CYCLE_MS - SHRINK_MS)) / SHRINK_MS, 0);
      value.style.transform = `scale(${1 + GROWTH * growing * (1 - shrinking)})`;
      frame = requestAnimationFrame(tick);
    };
    const stop = () => { cancelAnimationFrame(frame); frame = 0; };
    const sync = () => {
      const shouldRun = phone.matches && onScreen;
      if (shouldRun && !frame) { startedAt = performance.now(); frame = requestAnimationFrame(tick); }
      if (!shouldRun && frame) stop();
    };
    const observer = typeof IntersectionObserver === "function"
      ? new IntersectionObserver(([entry]) => { onScreen = entry.isIntersecting; sync(); })
      : null;
    observer?.observe(value);
    phone.addEventListener("change", sync);
    sync();
    return () => { stop(); observer?.disconnect(); phone.removeEventListener("change", sync); };
  }, []);

  return (
    <span className={styles.amount} data-peak={`${dollars(HEADLINE_MONTH_PEAK)}+`}>
      <span ref={valueRef} className={styles.amountValue}>{dollars(HEADLINE_MONTH_FLOOR)}+</span>
    </span>
  );
}

export function HeroPhoneHeadline() {
  return (
    <span className="text-[34px] md:hidden">
      <span className="text-foreground">Bettors are posting <HeadlineAmount /> months.</span>
      <br />
      <span className="text-primary">Upside finds the bets.</span>
    </span>
  );
}

function TrialButton({ cta }: { cta: "get_started_free" | "start_trial" }) {
  return (
    <a
      href="#pricing"
      data-plan-chooser="home_hero"
      onClick={() => trackMarketingCtaClick({ surface: "home_hero", cta, destination: "#pricing" })}
      className="btn-toplight press-scale flex h-[52px] w-full items-center justify-center gap-2 rounded-xl bg-accent-value text-[17px] font-semibold text-zinc-950 hover:bg-accent-value-strong"
    >
      Start a free trial
    </a>
  );
}

function UpgradeButton({ href }: { href: string }) {
  return (
    <Link
      href={href}
      onClick={() => trackMarketingCtaClick({ surface: "home_hero", cta: "upgrade_diamond", destination: "/pricing" })}
      className="btn-toplight press-scale flex h-[52px] w-full items-center justify-center gap-2 rounded-xl bg-accent-value text-[17px] font-semibold text-zinc-950 hover:bg-accent-value-strong"
    >
      {DIAMOND_UPGRADE_LABEL}
    </Link>
  );
}

export function HeroPhoneCtas() {
  const { subscriptions, isAuthenticated, isLoading } = usePublicAuth();
  const signedIn = !isLoading && isAuthenticated;
  const upgradeHref = signedIn ? diamondUpgradeHref(subscriptions) : null;
  const [planHref, setPlanHref] = useState("/learning-center/plan?from=homepage");
  useEffect(() => { setPlanHref(calloutDestination("/learning-center/plan?from=homepage", new URLSearchParams(window.location.search))); }, []);
  if (signedIn && isDiamondMember(subscriptions)) return null;
  return (
    <div className="mt-5">
      {upgradeHref
        ? <UpgradeButton href={upgradeHref} />
        : <div className={isLoading ? "[.nav-auth_&]:invisible" : undefined}><TrialButton cta="get_started_free" /></div>}
      {!signedIn && (
        <Link
          href={planHref}
          onClick={() => {
            phCapture("homepage_survey_cta_clicked", {
              surface: "home_hero", cta_placement: "hero_phone_button",
              page_version: HOMEPAGE_PLAN_PAGE_VERSION, destination: "/learning-center/plan",
            }, { send_instantly: true });
            trackMarketingCtaClick({ surface: "home_hero", cta: "find_betting_plan", cta_label: "see_how_much_money", destination: "/learning-center/plan" });
          }}
          className={`press-scale mt-3 flex h-[52px] w-full items-center justify-center rounded-xl border border-white/10 bg-card text-[17px] font-semibold text-foreground ${NAV_SIGNED_OUT_VARIANT_CLASS}`}
        >
          See how much money you can make
        </Link>
      )}
      {!upgradeHref && <p className={`mt-3 text-xs leading-relaxed text-muted-foreground ${isLoading ? NAV_SIGNED_OUT_VARIANT_CLASS : ""}`}>{TRIAL_TERMS_SHORT} · Cancel anytime</p>}
    </div>
  );
}

export function HeroPhoneCapture() {
  const [email, setEmail] = useState("");
  const [state, setState] = useState<UsState | null>(null);
  const [stateSource, setStateSource] = useState<StateSource>("unknown");
  const [submitting, setSubmitting] = useState(false);
  const [known, setKnown] = useState(false);
  const [justCaptured, setJustCaptured] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submittingRef = useRef(false);
  const stateEditedRef = useRef(false);

  useEffect(() => {
    if (hasCookie(EMAIL_CAPTURED)) { setKnown(true); return; }
    if (typeof window.matchMedia !== "function" || !window.matchMedia(PHONE_QUERY).matches) return;
    const controller = new AbortController();
    fetch("/api/marketing/email-capture-context", { cache: "no-store", signal: controller.signal })
      .then((response) => (response.ok ? response.json() : null))
      .then((context) => {
        if (!context) return;
        if (context.eligible !== true) { setKnown(true); return; }
        const detected = normalizeUsState(context.state);
        if (detected && !stateEditedRef.current) { setState(detected); setStateSource("detected"); }
      })
      .catch(() => {});
    return () => controller.abort();
  }, []);

  useEffect(() => {
    const onCapturedElsewhere = (event: Event) => {
      if ((event as CustomEvent).detail !== ANALYTICS_SURFACE) setKnown(true);
    };
    window.addEventListener(EMAIL_CAPTURED_EVENT, onCapturedElsewhere);
    return () => window.removeEventListener(EMAIL_CAPTURED_EVENT, onCapturedElsewhere);
  }, []);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    setError(null);
    phCapture("email_capture_submitted", { surface: ANALYTICS_SURFACE });
    try {
      const result = await captureCustomer(email.trim(), {
        surface: EMAIL_CAPTURE_SURFACE, state, stateSource,
        ph_distinct_id: phGetDistinctId() ?? undefined, attribution: getAttributionForEvent(),
        event_source_url: window.location.href,
      });
      if (!result.success) {
        setError(result.outcome === "rate_limited"
          ? "Too many attempts. Please wait a few minutes and try again."
          : "We couldn't save your email. Please try again.");
        phCapture("email_capture_failed", { surface: ANALYTICS_SURFACE });
        return;
      }
      setCookie(EMAIL_CAPTURED, 365);
      setKnown(true);
      setJustCaptured(true);
      window.dispatchEvent(new CustomEvent(EMAIL_CAPTURED_EVENT, { detail: ANALYTICS_SURFACE }));
      phCapture("email_capture_succeeded", { surface: ANALYTICS_SURFACE, outcome: result.outcome });
    } catch {
      setError("We couldn't save your email. Please try again.");
      phCapture("email_capture_failed", { surface: ANALYTICS_SURFACE });
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  if (known && !justCaptured) return null;

  if (known) {
    return (
      <div className="mt-10 rounded-2xl border border-white/10 bg-card p-4">
        <p role="status" aria-live="polite" className="mb-3 text-[15px] leading-snug text-foreground">
          You&apos;re on the list. Want the tools that find these bets?
        </p>
        <TrialButton cta="start_trial" />
        <p className="mt-3 text-xs leading-relaxed text-muted-foreground">{TRIAL_TERMS_SHORT} · Cancel anytime</p>
      </div>
    );
  }

  return (
    <div className="mt-10 rounded-2xl border border-white/10 bg-card p-4">
      <p className="mb-3 font-display text-lg font-medium leading-snug text-foreground">
        Get free, profitable {state ? `${state} ` : ""}bets sent to your inbox.
      </p>
      <form onSubmit={submit} className="space-y-2">
        <div className={`flex h-12 items-center rounded-xl bg-background ${styles.field}`} data-used={email ? "" : undefined}>
          <Mail aria-hidden className="ml-4 size-5 shrink-0 text-primary" />
          <label htmlFor="hero-phone-email" className="sr-only">Your email</label>
          <span aria-hidden="true" className={styles.caret} />
          <input
            id="hero-phone-email" type="email" inputMode="email" autoComplete="email" enterKeyHint="send"
            placeholder="Your email..." required maxLength={320} disabled={submitting} data-ph-mask
            value={email} onChange={(event) => { setEmail(event.target.value); setError(null); }}
            aria-invalid={Boolean(error)} aria-describedby={error ? "hero-phone-email-error" : undefined}
            className="h-full min-w-0 flex-1 bg-transparent px-3 text-base text-foreground placeholder:text-muted-foreground focus:outline-none"
          />
          <div className="relative mr-1 flex h-11 shrink-0 items-center gap-1 px-3 text-sm font-semibold text-foreground">
            <span aria-hidden="true">{state ?? "State"}</span>
            <ChevronDown aria-hidden className="size-4 text-muted-foreground" />
            <label htmlFor="hero-phone-state" className="sr-only">State (optional)</label>
            <select
              id="hero-phone-state" value={state ?? ""} disabled={submitting}
              onChange={(event) => { stateEditedRef.current = true; setState(normalizeUsState(event.target.value)); setStateSource("selected"); }}
              className="absolute inset-0 size-full cursor-pointer opacity-0"
            >
              <option value="">State (optional)</option>
              {Object.entries(US_STATES).map(([code, name]) => <option key={code} value={code}>{name}</option>)}
            </select>
          </div>
        </div>
        {error && <p id="hero-phone-email-error" role="alert" className="text-sm text-foreground">{error}</p>}
        <button
          type="submit" disabled={submitting}
          className="btn-toplight press-scale flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-accent-value text-base font-semibold text-zinc-950 hover:bg-accent-value-strong disabled:opacity-60"
        >
          {submitting ? <><Loader2 aria-hidden className="size-5 animate-spin" /> Saving…</> : <>Send me free bets <ArrowRight aria-hidden className="size-5" /></>}
        </button>
      </form>
    </div>
  );
}

const EAGER_CARDS = 2;
const RECORDS_START = 1;
const STORIES_START = RECORDS_START + MONTH_RECORDS.length;
const WAKE_EVENTS = ["scroll", "touchstart", "pointerdown", "keydown"] as const;

function ProfitPill({ label, amount }: { label: string; amount: string }) {
  return (
    <div className="rounded-full border border-accent-value/40 bg-background px-4 py-2 text-center">
      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-foreground/70">{label}</p>
      <p className="data-num text-[26px] font-semibold leading-tight text-accent-value">{amount}</p>
    </div>
  );
}

function Reviewer({ avatar, loading, name, tag, detail, verified = true }: { avatar: string; loading: "eager" | "lazy"; name: string; tag?: string; detail: string; verified?: boolean }) {
  return (
    <div className="mt-4 flex items-center gap-2.5">
      <PhoneOnlyImage className="block shrink-0">
        <Image src={avatar} alt="" width={36} height={36} loading={loading} className="size-9 rounded-full object-cover" />
      </PhoneOnlyImage>
      <div className="min-w-0">
        <p className="flex items-center gap-1 text-[15px] font-semibold leading-tight text-foreground">
          <span className="truncate">{name}</span>
          {verified && <BadgeCheck aria-label="Verified by Pikkit" role="img" className="size-4 shrink-0 text-primary" />}
        </p>
        <p className="mt-0.5 text-xs leading-snug text-muted-foreground">{tag ? `${tag} · ${detail}` : detail}</p>
      </div>
    </div>
  );
}

type Story = { name: string; handle: string; postedAt?: string; profit?: string; avatarSrc: string; imageSrc?: string; text: string };

function StoryCard({ story, label, loading, verified, fetchPriority }: { story: Story; label: string; loading: "eager" | "lazy"; verified?: boolean; fetchPriority?: "low" }) {
  return (
    <li className="flex w-[284px] shrink-0 snap-start flex-col rounded-2xl border border-white/10 bg-card p-4">
      {story.profit && <ProfitPill label={label} amount={story.profit} />}
      <Reviewer
        avatar={story.avatarSrc} loading={loading} verified={verified}
        name={story.name} detail={`${story.handle}${story.postedAt ? ` · ${story.postedAt}` : ""}`}
      />
      <p className="mt-3 line-clamp-3 text-sm leading-relaxed text-foreground/90">{story.text}</p>
      <div className="mt-auto pt-3">
        <div className="overflow-hidden rounded-xl border border-white/10 bg-background">
          <PhoneOnlyImage className="block">
            <Image src={story.imageSrc!} alt={`Result posted by ${story.name}`} width={480} height={640} loading={loading} fetchPriority={fetchPriority} sizes="252px" className="h-[300px] w-full object-contain" />
          </PhoneOnlyImage>
        </div>
      </div>
    </li>
  );
}

export function HeroPhoneProof() {
  const reviewsRef = useRef<HTMLElement>(null);
  const [warm, setWarm] = useState(false);
  const cardLoading = (index: number) => (index < EAGER_CARDS || warm ? "eager" : "lazy");

  useEffect(() => {
    if (typeof window.matchMedia !== "function" || !window.matchMedia(PHONE_QUERY).matches) return;
    const wake = () => {
      setWarm(true);
      WAKE_EVENTS.forEach((name) => window.removeEventListener(name, wake));
    };
    WAKE_EVENTS.forEach((name) => window.addEventListener(name, wake, { passive: true }));
    return () => WAKE_EVENTS.forEach((name) => window.removeEventListener(name, wake));
  }, []);

  useEffect(() => {
    const reviews = reviewsRef.current;
    if (!reviews || typeof window.matchMedia !== "function" || typeof IntersectionObserver !== "function") return;
    if (!window.matchMedia(PHONE_QUERY).matches) return;
    setLauncherHeld(true);
    const observer = new IntersectionObserver(
      ([entry]) => setLauncherHeld(entry.isIntersecting),
      { rootMargin: "0px 0px 100000px 0px" },
    );
    observer.observe(reviews);
    return () => { observer.disconnect(); setLauncherHeld(false); };
  }, []);

  return (
    <div className="md:hidden">
      <section ref={reviewsRef} aria-labelledby="hero-phone-proof-heading" className="mt-6">
        <h2 id="hero-phone-proof-heading" className="sr-only">Real bettors. Real receipts.</h2>
        <ul className="-mx-5 flex snap-x snap-mandatory scroll-px-5 gap-3 overflow-x-auto px-5 pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <StoryCard story={OPENING_STORY} label="Cashed out" loading={cardLoading(0)} verified={false} fetchPriority="low" />
          {MONTH_RECORDS.map((record, index) => (
            <li key={record.id} className="flex w-[284px] shrink-0 snap-start flex-col rounded-2xl border border-white/10 bg-card p-4">
              <ProfitPill label={record.metric} amount={`+$${record.amount.toLocaleString("en-US", { minimumFractionDigits: 2 })}`} />
              <Reviewer
                avatar={RECORD_DETAILS[record.id]?.avatar ?? "/images/sportsbooks/upside_logo.png"} loading={cardLoading(RECORDS_START + index)}
                name={record.name} tag={RECORD_DETAILS[record.id]?.note} detail={record.period}
              />
              <a
                href={record.source} target="_blank" rel="noopener noreferrer"
                onClick={() => phCapture("homepage_proof_opened", { surface: "home_hero_phone_results", image: record.src })}
                aria-label={`View ${record.name}’s original record`}
                className="mt-4 block overflow-hidden rounded-xl border border-white/10 bg-background"
              >
                <PhoneOnlyImage className="block">
                  <Image src={record.src} alt={record.title} width={record.width} height={record.height} loading={cardLoading(RECORDS_START + index)} fetchPriority={RECORDS_START + index < EAGER_CARDS ? "low" : undefined} sizes="252px" className="h-[372px] w-full object-contain" />
                </PhoneOnlyImage>
              </a>
            </li>
          ))}
          {STORIES.map((story, index) => (
            <StoryCard key={story.id} story={story} label="Profit posted" loading={cardLoading(STORIES_START + index)} />
          ))}
        </ul>
        <p className="mx-auto mt-4 max-w-[320px] text-center text-[13px] leading-relaxed text-muted-foreground">
          Posted on X by people using Upside. <BadgeCheck aria-hidden className="-mt-0.5 inline-block size-3.5 align-middle text-primary" /> Verified by Pikkit – a 3rd party betting tracker.
        </p>
      </section>
      <HeroPhoneCapture />
    </div>
  );
}
